import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { User, UserService } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { PreferredLangsSelectComponent } from '@klickdata/shared-components/src/preferred-langs-select/preferred-langs-select.component';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-user-perosonal-settings-and-language',
    templateUrl: './user-perosonal-settings-and-language.component.html',
    styleUrls: ['./user-perosonal-settings-and-language.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPerosonalSettingsAndLanguageComponent implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild(PreferredLangsSelectComponent) prefLangSelectComp: PreferredLangsSelectComponent;
    @Input()
    public set user(user: User) {
        this._user = user;
        if (user && this.settingsForm) {
            this.settingsForm.patchValue(
                {
                    pref_lang_ids: user.pref_lang_ids || [],
                    metadata: user.metadata || {},
                },
                { emitEvent: false }
            );
        }
    }
    public get user(): User {
        return this._user;
    }
    private _user: User;
    public authUser: User;
    public settingsForm: FormGroup;
    private destroy: Subject<boolean> = new Subject<boolean>();

    constructor(
        protected userService: UserService,
        protected auth: AuthService,
        protected message: MessageService,
        protected fb: FormBuilder
    ) {
        this.initializeForm();
    }

    private initializeForm(): void {
        this.settingsForm = this.fb.group({
            pref_lang_ids: [[], [Validators.required]],
            metadata: this.fb.group({
                isPlayAsGroupLeader: [false],
            }),
        });
    }

    ngOnInit(): void {
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy))
            .subscribe((user) => (this.authUser = user));

        this.auth
            .getCustomer()
            .pipe(takeUntil(this.destroy))
            .subscribe((customer) => {
                const isPlayAsGroupLeader = customer.metadata?.isPlayAsGroupLeader || false;
                const isPlayAsGroupLeaderFC = this.settingsForm.get('metadata.isPlayAsGroupLeader');
                if (isPlayAsGroupLeader) {
                    isPlayAsGroupLeaderFC.enable();
                } else {
                    isPlayAsGroupLeaderFC.disable();
                }
            });
    }
    ngAfterViewInit(): void {
        // Single handler for all form changes
        this.settingsForm.valueChanges
            .pipe(
                takeUntil(this.destroy),
                debounceTime(500),
                distinctUntilChanged((f1, f2) => Utils.isEqual(f1, f2)),
                switchMap((formValue) =>
                    this.userService.update({
                        id: this.user.id,
                        ...formValue,
                    })
                ),
                catchError((err) => {
                    if (err && err.error && err.error.error) {
                        this.message.openMessage(MessageErrorComponent, err.error.error.messages);
                    }
                    return of(null);
                })
            )
            .subscribe((updatedUser) => {
                if (updatedUser && this.authUser.id === updatedUser.id) {
                    this.auth.refreshToken();
                }
            });
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    /**
     * Check if form is valid
     */
    isFormValid(): boolean {
        return this.settingsForm.valid;
    }

    /**
     * Reset form to initial values
     */
    resetForm(): void {
        if (this.user) {
            this.settingsForm.patchValue({
                pref_lang_ids: this.user.pref_lang_ids || [],
                metadata: this.user.metadata || {},
            });
        }
    }
}
